import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Wallet, CheckCircle2, ArrowRight, Loader2, Wand2 } from "lucide-react";

const FIELDS = [
  {
    key: "amount_col",
    label: "Outstanding amount",
    help: "How much the customer still owes (per row).",
    required: true,
  },
  {
    key: "due_date_col",
    label: "Due date",
    help: "When the payment is expected.",
    required: true,
  },
  {
    key: "customer_col",
    label: "Customer name",
    help: "Who owes the money. Optional but improves risk-flagging.",
    required: false,
  },
  {
    key: "status_col",
    label: "Payment status",
    help: "Optional column like PAID / OVERDUE / OPEN. We use it to skip already-paid rows.",
    required: false,
  },
  {
    key: "paid_col",
    label: "Amount paid (optional)",
    help: "Only set if your CSV has invoice gross + amount paid as separate columns. Otherwise leave blank.",
    required: false,
  },
];


export default function CashflowMappingPanel({ source, onChange }) {
  const profileColumns = (source?.profile?.columns || []).map(c => c.name);
  const initial = source?.cashflow_columns || {};

  const [picks, setPicks] = useState({
    amount_col: initial.amount_col || "",
    due_date_col: initial.due_date_col || "",
    customer_col: initial.customer_col || "",
    status_col: initial.status_col || "",
    paid_col: initial.paid_col || "",
  });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [ready, setReady] = useState(Boolean(initial.amount_col && initial.due_date_col));

  // Phase 35A — only show this panel for CSV sources
  if ((source?.origin || "").toUpperCase() !== "CSV") return null;

  const set = (k, v) => setPicks(prev => ({ ...prev, [k]: v }));

  const autoSuggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.get(`/cashflow/sources/${source.id}/suggest-mapping`);
      const sug = r.data?.suggestion || {};
      const empty = Object.values(sug).every(v => !v);
      if (empty) {
        toast.error("Couldn't infer columns from the headers. Pick them manually.");
      } else {
        setPicks(prev => ({
          amount_col: sug.amount_col || prev.amount_col,
          due_date_col: sug.due_date_col || prev.due_date_col,
          customer_col: sug.customer_col || prev.customer_col,
          status_col: sug.status_col || prev.status_col,
          paid_col: sug.paid_col || prev.paid_col,
        }));
        const filled = Object.values(sug).filter(Boolean).length;
        toast.success(`Suggested ${filled} column${filled === 1 ? "" : "s"} — review then save.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-suggest failed");
    }
    setSuggesting(false);
  };

  const save = async () => {
    if (!picks.amount_col || !picks.due_date_col) {
      toast.error("Outstanding amount + due date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(picks).filter(([, v]) => v),
      );
      const r = await api.patch(`/cashflow/sources/${source.id}/mapping`, payload);
      setReady(Boolean(r.data?.ready));
      toast.success("Cash-flow mapping saved");
      if (onChange) onChange(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save mapping");
    }
    setSaving(false);
  };

  return (
    <Panel
      testId="cashflow-mapping-panel"
      title="// CASH-FLOW MAPPING"
      meta={<Wallet className="w-3.5 h-3.5 text-[#FFB000]" />}
    >
      <div className="p-5 space-y-4">
        <div>
          <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
            Tell DARA which columns hold the money and the dates. Once saved, you&apos;ll
            be able to run a cash-flow forecast on this source from the Cash-Flow page.
          </p>
          {ready && (
            <div className="mt-2 flex items-center gap-2 text-[11px] font-mono text-[#00E676]">
              <CheckCircle2 className="w-3.5 h-3.5" />
              READY · this source can drive a forecast
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(f => (
            <label key={f.key} className="block" data-testid={`mapping-field-${f.key}`}>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {f.label}{f.required ? " *" : ""}
              </span>
              <select
                value={picks[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
                data-testid={`mapping-select-${f.key}`}
                className="mt-1.5 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              >
                <option value="">—</option>
                {profileColumns.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <span className="font-mono text-[10px] text-zinc-600 mt-1 block leading-relaxed">{f.help}</span>
            </label>
          ))}
        </div>

        <div className="border-t border-zinc-800 pt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="font-mono text-[10px] text-zinc-600">
            Status text matched against PAID/OVERDUE/OPEN/AUTHORISED tokens — case insensitive.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={autoSuggest}
              disabled={suggesting || saving}
              data-testid="auto-suggest-mapping-btn"
              className="border border-[#00BFFF]/40 text-[#00BFFF] px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-[#00BFFF]/10 transition-colors disabled:opacity-40 flex items-center gap-2"
              title="Use column names to auto-fill the mapping"
            >
              {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              {suggesting ? "INFERRING…" : "AUTO-SUGGEST"}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !picks.amount_col || !picks.due_date_col}
              data-testid="save-mapping-btn"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              {saving ? "SAVING…" : "SAVE MAPPING"}
            </button>
          </div>
        </div>
      </div>
    </Panel>
  );
}
